/**
 * Componente: WahaStatusCards
 * Cards com contadores de status das sessões WAHA
 * 
 * @version 1.0.0
 * @created 2026-01-05
 */

import { Card, CardContent } from '@/components/ui/card';
import { 
  CheckCircle, 
  XCircle, 
  Loader2, 
  AlertTriangle, 
  Ghost, 
  AlertCircle 
} from 'lucide-react';
import type { WahaSummary } from '@/hooks/useWahaMonitor';

interface WahaStatusCardsProps {
  summary: WahaSummary;
  isLoading?: boolean;
}

/**
 * Cards de status das sessões WAHA
 */
export function WahaStatusCards({ summary, isLoading }: WahaStatusCardsProps) {
  // Configuração dos cards
  const cards = [
    {
      label: 'WORKING',
      value: summary.working,
      icon: CheckCircle,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      description: 'Conectadas e funcionando',
    },
    {
      label: 'STOPPED',
      value: summary.stopped,
      icon: XCircle,
      color: 'text-destructive',
      bgColor: 'bg-destructive/10',
      description: 'Paradas/desconectadas',
    },
    {
      label: 'STARTING',
      value: summary.starting,
      icon: Loader2,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10',
      description: 'Iniciando ou aguardando QR',
    },
    {
      label: 'FAILED',
      value: summary.failed,
      icon: AlertTriangle,
      color: 'text-destructive',
      bgColor: 'bg-destructive/10',
      description: 'Com erro',
    },
    {
      label: 'Órfãs',
      value: summary.orphans,
      icon: Ghost,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      description: 'Sem inbox vinculada',
    },
    {
      label: 'Divergentes',
      value: summary.mismatches,
      icon: AlertCircle,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-600/10',
      description: 'Status diferente do local',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <Card key={card.label} className="relative overflow-hidden">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    {card.label}
                  </p>
                  <p className="text-2xl font-bold mt-1">
                    {isLoading ? '-' : card.value}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {card.description}
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${card.bgColor}`}>
                  <Icon className={`h-5 w-5 ${card.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
